
import moment from 'moment';
import { forwardRef, useEffect, useRef, useState, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router';
import { usePagination, useRowSelect, useTable } from 'react-table';
import { toast } from 'react-toastify';
import { useAppContext } from '../../../appProvider/contextProvider';
import NoDataImage from '../../../assets/images/illustration.svg';
import HelperComponent from '../../../components/HelperComponent/HelperComponent';
import DeletePrompt from '../../../components/Propmts/deletePrompt';
import { deleteScheduledCampaigns, getScheduledCampaigns } from '../../../services/campaignService';


const ScheduleTypes = {
    'IMMED': 'Send Immediately',
    'SCHED': 'Scheduled Time',
    'BYSTZ': 'As Per Subscriber Time Zone',
}


const columns =  [
    {
        Header: 'Title',
        accessor: 'title', // accessor is the "key" in the data
    },
    {
        Header: 'Message',
        accessor: 'message', // accessor is the "key" in the data
    },
    {
        Header: 'Date Created',
        accessor: 'created_at', // accessor is the "key" in the data
        Cell: (props) => moment(props.value).format('MMM DD, YYYY HH:mm'),
    },
    {
        Header: 'Scheduled For',
        accessor: 'scheduled_at', // accessor is the "key" in the data
        Cell: (props) => moment(props.value).format('MMM DD, YYYY HH:mm'),
    },
    {
        Header: 'Schedule Time Type',
        accessor: 'type', // accessor is the "key" in the data
        Cell: (props) => ScheduleTypes[props.value],
    }
];

const IndeterminateCheckbox = forwardRef(
    ({ indeterminate, id, ...rest }, ref) => {
      const defaultRef = useRef()
      const resolvedRef = ref || defaultRef
  
      useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
      }, [resolvedRef, indeterminate])
  
      return (
        <div className="custom-control custom-checkbox">
            <input type="checkbox" className="custom-control-input" id={id} ref={resolvedRef} {...rest} />
            <label className="custom-control-label" htmlFor={id}></label>
        </div>
      )
    }
) 

const ScheduleCampaign = () => {

    const history = useHistory();

    const {selectedWebsite,
        isMutating, 
        isFetching,
        setIsBgDisabled
    } = useAppContext();

    const [scheduledCampaigns, setScheduledCampaigns] = useState([]);

    const [searchString , setSearchString] = useState('');

    
    const [deletePrompt, setDeletePrompt] = useState(false);
    
    const [ noOfRecords, setNoOfRecords ] = useState(0);
    const records = useMemo(() => {
        if(searchString) {
            const search = new RegExp(`${searchString}`, 'i');
            return scheduledCampaigns.filter(({title}) => title.match(search)).length;
        }
        return noOfRecords;
    }, [searchString, noOfRecords])

    useQuery(
        ['fetchScheduledCampaigns', selectedWebsite?.id], 
        () => getScheduledCampaigns(selectedWebsite?.id),
        {
            enabled: !!selectedWebsite,
            onSuccess: (scheduledCampaigns) => {
                if(scheduledCampaigns.errors) {

                } else {
                    setNoOfRecords(scheduledCampaigns.length)
                    setScheduledCampaigns(scheduledCampaigns);
                }
            }
        }
    );


    const scheduledCampaignsInstance = useTable({
            columns,
            data: scheduledCampaigns,
        }, 
        usePagination,
        useRowSelect,
        hooks => {
        hooks.visibleColumns.push(columns => [
          // Let's make a column for selection
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox id={'header'} {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) =>(
              <div>
                <IndeterminateCheckbox id={row?.original?.id} {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columns,
        ])
    });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
    } = scheduledCampaignsInstance;
      


    const getSearchedString = (cell, row) => {
        const search = new RegExp(`${searchString}`, 'g');
        if(searchString.length > 0 && row.original.title.match(search)) {
    
            return  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
        } else if(searchString.length<= 0) {
    
            return  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
        }

    }

    const deleteScheduled = useMutation(() => {
        const ids = selectedFlatRows.map(({original}) => original.id);
        return deleteScheduledCampaigns(selectedWebsite?.id, {ids});
    }, {
        onSuccess: (data, variables) => {
            if(data.errors || data.error) {

            } else {
                
                // const leftCampaigns = scheduledCampaigns.filter(({id}))
                const campaign_ids = selectedFlatRows.map(({original}) => original.id);
                const leftCampaigns = scheduledCampaigns.filter((campaign) => !campaign_ids.includes(campaign.id));
                setScheduledCampaigns(leftCampaigns);
                toast.success('Selected scheduled notifications deleted successfully');
            }
        }
    })


    const onDelete = (event) => {
        event.preventDefault();
        setDeletePrompt(false);
        setIsBgDisabled(false);
        deleteScheduled.mutate();
    }

    const onCancel = () =>{
        setDeletePrompt(false); 
        setIsBgDisabled(false);
        // setSegmentIdSelected(null)
    }
    

    const onClickDelete = () => {
        setIsBgDisabled(true);
        setDeletePrompt(true);
    }

    return   <>
    { deletePrompt && <DeletePrompt onDelete={onDelete} onCancel={onCancel} />}
    <div className="panel full-height">
        <div className="panel-header">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
                <h3 className="">
                    Scheduled Campaigns
                    <HelperComponent 
                        component={'Scheduled Campaigns'} 
                        href="https://chirpyweb.com/documentation_posts/scheduled-campaigns/" />    
                </h3>
                <div>
                    {selectedWebsite?.role !== 'view' && <button className="btn btn-danger" 
                        disabled={selectedFlatRows.length <= 0 } 
                        onClick={() => onClickDelete()}
                    ><i className="icon icon-delete mr-2"></i>Delete Selected</button>}
                </div>
            </div>                                
        </div>
        <div className="panel-body d-flex flex-column full-height scrollable_y">
            <div className="d-flex flex-column full-height">
                <div className="row justify-content-between align-items-start">                                    
                    <div className="col col-md-6 col-lg-4">
                        <div className="form-group">
                            <div className="input-addon input-group-right">
                                <i className="icon icon-search"></i>
                                <input 
                                    type="search" 
                                    value={searchString} 
                                    onChange={({target}) => {setSearchString(target.value)}} 
                                    placeholder="Search" 
                                    className="form-control"/>                        
                            </div>  
                        </div>
                    </div>                            
                </div>
                <div className="table-responsive br-0 full-height">
                    <table className="table table-hover br-0" {...getTableProps()}>
                        <thead>
                            {
                                headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {
                                            headerGroup.headers.map(column => (
                                                <th {...column.getHeaderProps()}>
                                                    {column.render('Header')}
                                                </th>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {   // Loop over the table rows
                                rows.map(row => {
                                    // Prepare the row for display
                                    prepareRow(row)
                                    return (
                                    // Apply the row props
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map(cell =>  getSearchedString(cell, row))}
                                        </tr>
                                    )
                                })
                            }
                            {
                               (!isFetching && !isMutating && scheduledCampaigns.length <= 0) &&  <tr>
                                <td colSpan="8" align="center">
                                        <div className="d-flex flex-column align-items-center justify-content-center mt-5">
                                            <img src={NoDataImage} className="illustration-image" />
                                            <h5 className="medium text-dark mt-2">No campaign sent, please try sending one</h5>
                                            <button className="btn btn-primary my-2" onClick={() => history.push('/create-campaign')}>Create Campaign</button>
                                        </div>                                                    
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <p className="mb-1">Total {records} entries</p>
                </div>   
            </div>                                                       
        </div>
    </div>

        </>

};


export default ScheduleCampaign;